import { lazy, Suspense, useEffect, useMemo } from 'react';
import { initSentry } from '@/services/observability/SentryService';

// Initialize Sentry before any rendering — captures mount-phase errors
initSentry();

import { useShallow } from 'zustand/react/shallow';
import { useStore } from './store';
// Lazy-load login and legal pages — they're never needed by authenticated users (ISSUE-1203)
const LoginFormLazy = lazy(() => import('./components/auth/LoginForm'));
const PrivacyPolicy = lazy(() => import('@/modules/legal/pages/LegalPages').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('@/modules/legal/pages/LegalPages').then(m => ({ default: m.TermsOfService })));
import { STANDALONE_MODULES, type ModuleId } from './constants';
import { useURLSync } from '@/hooks/useURLSync';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useMobile } from '@/hooks/useMobile';
import { useGlobalShortcutsModal } from '@/components/shared/GlobalKeyboardShortcuts';
import { useRemoteCommandListener } from '@/hooks/useRemoteCommandListener';
import { useSurfaceIcon } from '@/hooks/useSurfaceIcon';
import { useConnectivityMonitor } from '@/hooks/useConnectivityMonitor';
import { useAutoSleep } from '@/hooks/useAutoSleep';
import { useWorkspaceSync } from '@/hooks/useWorkspaceSync';
import { useBugReportShortcut } from '@/modules/debug/useBugReportShortcut';
import { LoadingFallback } from '@/core/components/LoadingFallbacks';
import { cleanupLocalStorage } from '@/lib/storageHealth';
import { flushFounderFunnelQueue } from '@/services/founders/founderFunnel';
import {
    buildMobileRemoteUrl,
    isRemoteSurfaceDevice,
    isStudioExecutorSurface,
    isMobileRemoteHost,
    isMobileRemoteBypassPath,
    isMobileRemotePath,
    shouldUseMobileRemoteSurface,
} from '@/modules/mobile-remote/routing';
import { MobileRemoteProviders } from '@/modules/mobile-remote/MobileRemoteProviders';
import '@/core/i18n'; // Initialize i18next — must run before any component renders
import { AppInitializationProvider } from '@/providers/AppInitializationProvider';
import { AuthInitializationProvider } from '@/providers/AppInitializationProvider';
import { featureFlags } from '@/config/featureFlags';
import { getPlatformOAuthCallbackProvider } from '@/modules/analytics/components/platformOAuthCallbackRoute';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PrintSpecDialog } from '@/components/ui/PrintSpecDialog';

const AppShell = lazy(() => import('./AppShell'));
const BugReportDialog = lazy(() => import('@/modules/debug/BugReportDialog').then(m => ({ default: m.BugReportDialog })));
const InstagramOAuthCallback = lazy(() => import('@/modules/analytics/components/InstagramOAuthCallback').then(m => ({ default: m.InstagramOAuthCallback })));
const PlatformOAuthCallback = lazy(() => import('@/modules/analytics/components/PlatformOAuthCallback').then(m => ({ default: m.PlatformOAuthCallback })));
const TaxFormUploadPage = lazy(() => import('@/modules/finance/pages/TaxFormUploadPage').then(m => ({ default: m.TaxFormUploadPage })));
const PreSaveLandingPage = lazy(() => import('@/modules/marketing/components/PreSaveLandingPage'));
const MobileRemote = lazy(() => import('@/modules/mobile-remote/MobileRemote'));

function DevPortWarning() {
    const port = window.location.port;
    if (!import.meta.env.DEV || port === '4243') return null;

    return (
        <div className="fixed bottom-4 right-4 z-[9999] bg-red-600 text-white px-3 py-2 rounded-lg shadow-lg text-xs font-bold border border-red-400 animate-pulse">
            indii.music — Web-Only Mode ({port})
            <br />
            <span className="font-normal opacity-80 text-[10px]">Use port :4243 for full Studio experience</span>
        </div>
    );
}

function PublicLegalPage({ type }: { type: 'privacy' | 'terms' }) {
    return (
        <div className="min-h-screen w-screen overflow-y-auto bg-black text-white">
            <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-6">
                <a href="/" className="text-sm font-semibold text-gray-400 transition-colors hover:text-white">
                    indii.music
                </a>
                <a href="/login" className="text-sm font-semibold text-gray-400 transition-colors hover:text-white">
                    Sign in
                </a>
            </div>
            <Suspense fallback={<LoadingFallback />}>
                {type === 'privacy' ? <PrivacyPolicy /> : <TermsOfService />}
            </Suspense>
        </div>
    );
}

function UnauthenticatedApp() {
    const location = useLocation();

    useEffect(() => {
        const isRoot = location.pathname === '/' || location.pathname === '';
        const isWebProd = typeof window !== 'undefined' && window.location.hostname === 'founder.indii.music';

        if (isRoot && isWebProd) {
            window.location.replace('https://indii.music');
        }
    }, [location.pathname]);

    const isRoot = location.pathname === '/' || location.pathname === '';
    const isWebProd = typeof window !== 'undefined' && window.location.hostname === 'founder.indii.music';

    if (isRoot && isWebProd) {
        return <LoadingFallback />;
    }

    return (
        <Suspense fallback={<LoadingFallback />}>
            <LoginFormLazy />
        </Suspense>
    );
}

function StudioApplication({ mobile }: { mobile: ReturnType<typeof useMobile> }) {
    const location = useLocation();
    const { currentModule, user, authLoading } = useStore(
        useShallow(state => ({
            currentModule: state.currentModule,
            user: state.user,
            authLoading: state.authLoading,
        }))
    );

    const shortcutsModal = useGlobalShortcutsModal();

    // Bug reporting keyboard shortcuts (Ctrl+Shift+B for bugs, Ctrl+Shift+F for features)
    useBugReportShortcut();

    // Monitor actual connectivity in Electron — fixes stuck offline state
    useConnectivityMonitor();

    // Auto-sleep the desktop to the tray after configurable idle (Electron only)
    useAutoSleep();

    // Cross-device workspace sync (push/pull, debounced)
    useWorkspaceSync();

    // Session metadata and the active session's append-only message stream must
    // be live on every signed-in surface, not only after opening the archive.
    const userId = user?.uid;
    useEffect(() => {
        if (!userId) return;
        useStore.getState().loadSessions().catch(error => {
            console.error('[App] Failed to start live agent-session sync:', error);
        });
    }, [userId]);

    const isMobileRemoteBypassRoute = isMobileRemoteBypassPath(location.pathname);
    const { isAnyPhone } = mobile;
    const isStudioExecutor = isStudioExecutorSurface(currentModule, false);

    // Remote Relay: only the actual Studio surface may publish presence or
    // consume Studio-owned work. Controller pages can only produce commands.
    useRemoteCommandListener(isStudioExecutor);

    // ISSUE-1164: distinct tab/home-screen icon for the mobile-remote Controller surface.
    const isInstagramOAuthCallback = useMemo(
        () => (location.pathname.replace(/\/+$/, '') || '/') === '/auth/instagram/callback',
        [location.pathname],
    );
    const platformOAuthCallbackProvider = useMemo(
        () => getPlatformOAuthCallbackProvider(location.pathname),
        [location.pathname],
    );
    // URL sync must not rewrite public, auth, callback, or Controller routes.
    useURLSync({
        disabled: isMobileRemoteBypassRoute,
    });

    // Determine if current module should show chrome (sidebar, command bar, etc.)
    const showChrome = useMemo(
        () => !STANDALONE_MODULES.includes(currentModule as ModuleId),
        [currentModule]
    );

    // SSR-safe media query for desktop detection
    const isDesktop = useMediaQuery('(min-width: 768px)');

    const signedInContent = isInstagramOAuthCallback ? (
                <Suspense fallback={<LoadingFallback />}><InstagramOAuthCallback /></Suspense>
            ) : platformOAuthCallbackProvider ? (
                <Suspense fallback={<LoadingFallback />}>
                    <PlatformOAuthCallback provider={platformOAuthCallbackProvider} />
                </Suspense>
            ) : (
                <Suspense fallback={<LoadingFallback />}>
                    <AppShell
                        activeModule={currentModule}
                        activeShowChrome={showChrome}
                        isDesktop={isDesktop}
                        isAnyPhone={isAnyPhone}
                        shortcutsModal={shortcutsModal}
                    />
                    <BugReportDialog />
                </Suspense>
            );

    return (
        <AuthInitializationProvider>
            {/*
             * Workspace sync can request confirmation from this component's
             * effect before the lazy AppShell has mounted. Keep the one
             * callable Root at this stable authenticated-surface boundary so
             * every ConfirmDialog.call() has a live Root on the first effect.
             */}
            <ConfirmDialog />
            <PrintSpecDialog />
            {authLoading || !user ? (
                authLoading ? <LoadingFallback /> : <UnauthenticatedApp />
            ) : (
                <AppInitializationProvider>{signedInContent}</AppInitializationProvider>
            )}
            {import.meta.env.DEV && <DevPortWarning />}
        </AuthInitializationProvider>
    );
}

/**
 * Surface router. Public pages and the standalone Controller stop here so
 * they never mount authenticated Studio listeners, account hydration, or
 * background agents.
 */
export default function App() {
    const location = useLocation();
    const mobile = useMobile();
    const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
    const shouldUseRemoteSurface = shouldUseMobileRemoteSurface({
        hostname: typeof window === 'undefined' ? '' : window.location.hostname,
        pathname: location.pathname,
        isElectron,
        isRemoteDevice: isRemoteSurfaceDevice(mobile),
    });

    const publicLegalPage = useMemo(() => {
        const path = location.pathname.replace(/\/+$/, '') || '/';
        if (path === '/privacy' || path === '/legal/privacy') return 'privacy';
        if (path === '/terms' || path === '/legal/terms') return 'terms';
        return null;
    }, [location.pathname]);
    const isTaxFormUploadPage = useMemo(
        () => (location.pathname.replace(/\/+$/, '') || '/') === '/tax-form-upload',
        [location.pathname],
    );
    const preSaveCampaignId = useMemo(() => {
        const match = location.pathname.match(/^\/presave\/([A-Za-z0-9_-]{8,128})\/?$/);
        return match?.[1] ?? null;
    }, [location.pathname]);

    // Defer non-critical startup work to avoid blocking first content. Remote
    // Config is deliberately initialized on every surface so kill switches
    // and rollout gates are not stuck at build-time defaults.
    useEffect(() => { cleanupLocalStorage(); }, []);
    useEffect(() => { flushFounderFunnelQueue(); }, []);
    useEffect(() => { void featureFlags.initialize(); }, []);

    useSurfaceIcon(shouldUseRemoteSurface);

    // The public app subdomain is the canonical Controller surface. Preserve
    // the one-time handoff query while moving legacy/mobile Studio URLs there.
    useEffect(() => {
        if (!shouldUseRemoteSurface || isElectron || typeof window === 'undefined') return;

        const hostname = window.location.hostname;
        const isLocal =
            hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname.endsWith('.local');
        if (isLocal) return;

        const tryRedirect = () => {
            // A viewport that hasn't finished laying out yet can report 0x0, which
            // satisfies every "max-width" media query and misclassifies a desktop
            // browser as a phone. Real devices are never 0x0. Skip this one-time,
            // hard `location.replace` redirect until the window has a real size,
            // rather than stranding a desktop visitor on the Controller surface.
            if (window.innerWidth === 0 || window.innerHeight === 0) return false;

            if (!isMobileRemoteHost(hostname) || !isMobileRemotePath(window.location.pathname)) {
                window.location.replace(
                    buildMobileRemoteUrl(window.location.search, window.location.hash)
                );
            }
            return true;
        };

        if (!tryRedirect()) {
            const onResize = () => {
                if (tryRedirect()) {
                    window.removeEventListener('resize', onResize);
                }
            };
            window.addEventListener('resize', onResize);
            return () => window.removeEventListener('resize', onResize);
        }
    }, [shouldUseRemoteSurface, isElectron, location.pathname]);

    let surface: React.ReactNode;
    if (shouldUseRemoteSurface) {
        surface = (
            <MobileRemoteProviders>
                <Suspense fallback={<LoadingFallback />}><MobileRemote /></Suspense>
            </MobileRemoteProviders>
        );
    } else if (publicLegalPage) {
        surface = <PublicLegalPage type={publicLegalPage} />;
    } else if (isTaxFormUploadPage) {
        surface = <Suspense fallback={<LoadingFallback />}><TaxFormUploadPage /></Suspense>;
    } else if (preSaveCampaignId) {
        surface = (
            <Suspense fallback={<LoadingFallback />}>
                <PreSaveLandingPage campaignId={preSaveCampaignId} />
            </Suspense>
        );
    } else {
        surface = <StudioApplication mobile={mobile} />;
    }

    return (
        <>
            {surface}
            {import.meta.env.DEV && shouldUseRemoteSurface && <DevPortWarning />}
        </>
    );
}
